import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';

import { Config } from '../../common/config';
import { formatRefundSerialDisplay } from '../../utils/formatGiftSerial';
import LeftMenu from '../../components/leftMenu';
import Pagination from '../../components/pagination';
import { formatNumNumber } from '../../components/utils';
import translations from '../../data/translation';
import LocaleDateInput from '../../components/LocaleDateInput';
import useAxiosFetch from '../../hooks/useAxiosFetch'; // 경로는 실제 파일 위치에 맞게 수정
import useStoreNo from '../../hooks/useStoreNo';

export default function GiftRefund() {
  const { fetchData } = useAxiosFetch();

  const userInfo = useSelector(state => state.userInfo);
  const storeNo = useStoreNo();
  const language = useSelector(state => state.language.language);
  const t = translations[language];
  const [searchCondition, setSearchCondition] = useState({
    flag: false,
    pageSize: Config.PAGE_SIZE_LIST[0],
    sortOrd: '',
    startDttm: '',
    endDttm: '',
    applicantType: '',
    accountHolder: '',
    accountNo: '',
  });

  // 서버 refund.applicantType 값: gifter(구매자) / giftee(수신자) / owner(상점주).
  // 예전에는 gifter가 아니면 모두 수신자 환불로 보여 상점주 환불이 수신자 환불로 잘못 나왔다.
  const getApplicantTypeLabel = type => {
    if (!type) return null;
    const key = type.toLowerCase();
    if (key === 'gifter') return t.refundTypeGifter;
    if (key === 'giftee') return t.refundTypeGiftee;
    if (key === 'owner') return t.refundTypeOwner;
    return type;
  };
  const [total, setTotal] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);

  // JWT refresh handled centrally in useAxiosFetch

  const getData = useCallback(async () => {
    try {
      let params = '?storeNo=' + userInfo.storeNo;
      params += '&pageNo=' + currentPage;
      params += '&pageSize=' + searchCondition.pageSize;
      if (searchCondition.sortOrd.length > 0) {
        params += '&sortOrd=' + searchCondition.sortOrd;
      }
      if (searchCondition.startDttm.length > 0) {
        params += '&startDttm=' + searchCondition.startDttm;
      }
      if (searchCondition.endDttm.length > 0) {
        params += '&endDttm=' + searchCondition.endDttm;
      }
      if (searchCondition.accountNo.length > 0) {
        params += '&accountNo=' + searchCondition.accountNo;
      }
      if (searchCondition.accountHolder.length > 0) {
        params += '&accountHolder=' + searchCondition.accountHolder;
      }
      if (searchCondition.applicantType.length > 0) {
        params += '&applicantType=' + searchCondition.applicantType;
      }

      const ret = await fetchData({
        url: Config.API_SERVER + 'refund' + params,
        method: 'get',
      });
      if (ret?.data?.data) {
        const count = ret.data.data.total;
        const tp = Math.ceil(count / searchCondition.pageSize);

        setTotalPage(tp);
        setTotal(count);
        setData(ret.data.data.list);
      }
      setSearchCondition(prev => ({ ...prev, flag: false }));
    } catch (error) {
      console.error('Error getData:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    searchCondition.pageSize,
    searchCondition.sortOrd,
    searchCondition.startDttm,
    searchCondition.endDttm,
    searchCondition.accountNo,
    searchCondition.accountHolder,
    searchCondition.applicantType,
    searchCondition.flag,
    userInfo.storeNo,
  ]);

  const pageChange = page => {
    setCurrentPage(page);
  };

  const getSearch = () => {
    setSearchCondition({ ...searchCondition, flag: true });
  };

  const getClear = () => {
    setSearchCondition({
      ...searchCondition,
      sortOrd: '',
      startDttm: '',
      endDttm: '',
      applicantType: '',
      giftIdOrd: '',
      accountHolder: '',
      accountNo: '',
    });
  };

  useEffect(() => {
    if (userInfo?.userNo !== '') {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    searchCondition.flag,
    searchCondition.applicantType,
    userInfo?.userNo,
  ]);

  return (
    <>
      <section className="location_box location_box_mypage">
        <ul className="location_001">
          <li>
            <NavLink to={storeNo ? `/${storeNo}/gift-list` : '/gift-list'}>
              {t.home}
            </NavLink>
          </li>
          <li>
            <NavLink to={storeNo ? `/${storeNo}/gift-list` : '/gift-list'}>
              {t.giftManagement}
            </NavLink>
          </li>
          <li>{t.refundHistory}</li>
        </ul>
      </section>

      <main>
        <section className="contants_box contants_box_mypage">
          <LeftMenu pageName="gift" />

          <section className="rightcontant_box">
            <h2 className="tit_01">{t.refundHistory}</h2>

            <article className="refund_search_card">
              <div className="search_row">
                <div className="search_field">
                  <span className="field_label">{t.searchPeriod}</span>
                  <LocaleDateInput
                    value={searchCondition.startDttm}
                    onChange={val =>
                      setSearchCondition({
                        ...searchCondition,
                        startDttm: val,
                      })
                    }
                    placeholderText={
                      language === 'ko' ? '시작 날짜' : 'Start date'
                    }
                  />
                  <span className="date_sep">~</span>
                  <LocaleDateInput
                    value={searchCondition.endDttm}
                    onChange={val =>
                      setSearchCondition({
                        ...searchCondition,
                        endDttm: val,
                      })
                    }
                    placeholderText={
                      language === 'ko' ? '종료 날짜' : 'End date'
                    }
                  />
                </div>
                <div className="search_field">
                  <span className="field_label">{t.giftSerial}</span>
                  <select
                    value={searchCondition.giftIdOrd}
                    onChange={event =>
                      setSearchCondition({
                        ...searchCondition,
                        giftIdOrd: event.target.value,
                      })
                    }
                  >
                    <option value="">{t.giftSerial}</option>
                    <option value="desc">{t.descendingOrder}</option>
                    <option value="asc">{t.ascendingOrder}</option>
                  </select>
                </div>
                <div className="search_field">
                  <span className="field_label">{t.accountHolder}</span>
                  <select
                    value={searchCondition.accountHolder}
                    onChange={event =>
                      setSearchCondition({
                        ...searchCondition,
                        accountHolder: event.target.value,
                      })
                    }
                  >
                    <option value="">{t.accountHolder}</option>
                    <option value="desc">{t.descendingOrder}</option>
                    <option value="asc">{t.ascendingOrder}</option>
                  </select>
                </div>
                <div className="search_btns">
                  <button
                    type="button"
                    className="button_05 button_black"
                    onClick={getSearch}
                  >
                    {t.search}
                  </button>
                  <button
                    type="button"
                    className="button_05 button_gray_line"
                    onClick={getClear}
                  >
                    {t.reset}
                  </button>
                </div>
              </div>
              <div className="refund_filter_group">
                <span className="filter_label">{t.refundType}</span>
                {[
                  { value: '', label: t.refundFilterAll },
                  { value: 'gifter', label: t.refundTypeGifter },
                  { value: 'giftee', label: t.refundTypeGiftee },
                  { value: 'owner', label: t.refundTypeOwner },
                ].map(({ value, label }) => (
                  <button
                    key={value || 'all'}
                    type="button"
                    className={`filter_btn ${
                      searchCondition.applicantType === value ? 'active' : ''
                    }`}
                    onClick={() => {
                      setSearchCondition(prev => ({
                        ...prev,
                        applicantType: value,
                        flag: true,
                      }));
                      setCurrentPage(1);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </article>

            <div className="table_wrapper">
              <table className="table_box_01">
                <colgroup>
                  <col width="60px" />
                  <col width="100px" />
                  <col width="220px" />
                  <col width="100px" />
                  <col width="120px" />
                  <col width="100px" />
                </colgroup>

                <thead>
                  <tr>
                    <th>{t.no}</th>
                    <th>{t.refundType}</th>
                    <th>{t.giftSerial}</th>
                    <th>{t.refundAmount}</th>
                    <th>{t.accountHolder}</th>
                    <th>{t.status}</th>
                  </tr>
                </thead>

                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        style={{
                          padding: '48px',
                          textAlign: 'center',
                          color: '#94a3b8',
                          fontSize: '15px',
                        }}
                      >
                        {language === 'ko'
                          ? '검색 결과가 없습니다.'
                          : 'No search results.'}
                      </td>
                    </tr>
                  ) : (
                    data.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>
                          {getApplicantTypeLabel(item.applicantType) ? (
                            <span
                              className={`state-badge applicant-${(
                                item.applicantType || ''
                              ).toLowerCase()}`}
                            >
                              {getApplicantTypeLabel(item.applicantType)}
                            </span>
                          ) : (
                            <span className="cell-empty">—</span>
                          )}
                        </td>
                        <td>
                          {(() => {
                            const serial = formatRefundSerialDisplay(item);
                            return serial !== '-' ? (
                              serial
                            ) : (
                              <span className="cell-empty">—</span>
                            );
                          })()}
                        </td>
                        <td>
                          {item.amount != null ? (
                            `${formatNumNumber(item.amount)}${t.currency}`
                          ) : (
                            <span className="cell-empty">—</span>
                          )}
                        </td>
                        <td>
                          {item.accountHolder ? (
                            item.accountHolder
                          ) : (
                            <span className="cell-empty">—</span>
                          )}
                        </td>
                        <td>
                          {item.state ? (
                            <span
                              className={`state-badge refund-${
                                item.state || ''
                              }`}
                            >
                              {t.stateDesc[item.stateDesc] || item.stateDesc}
                            </span>
                          ) : (
                            <span className="cell-empty">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <ul className="table_02">
              <li>
                {t.total} : {formatNumNumber(total)} {t.count}
              </li>
            </ul>

            <ul className="page_box">
              <Pagination totalPage={totalPage} onPageChange={pageChange} />
            </ul>
          </section>
        </section>
      </main>
    </>
  );
}
